/***************************************************

The form-based editor will use the provided selector
to retrieve, select a form and present that form to
the user for edit.  the user will then have the
option to Save, or cancel.  

This form is intended to provided simple form-based
ui editing, such as that required for entity editing

****************************************************/

import * as React from "react";
import * as request from "superagent";
import * as jsonpatch from "../components/jsonpatch";
import withStyles, {
  WithStyles,
  StyleRules,
} from "@material-ui/core/styles/withStyles";
import { Theme } from "@material-ui/core/styles";
import LoadingView from "~common/components/loading-view";
import ErrorView from "~common/components/error-view";
import SuccessMessageView from "~common/components/success-view";
import { Definition } from "~common/lib/types";
import { Button } from "@material-ui/core";
import { Paper } from "@material-ui/core";
import URLManager from "~common/lib/url-manager";
import * as _ from "lodash";
import {
  setValueAtPath,
  getValueAtPath,
  baseConditionalEval,
} from "~common/form/helper";
import DefinitionSection from "~common/form/definition-section";
import RendererRegistry from "~common/lib/renderers";
import SimpleConfirmationModal from "~common/components/simple-confirmation-modal";
import { PredefinedField } from "~common/lib/types";

import { ReCaptcha, loadReCaptcha } from "react-recaptcha-v3";
import ReCaptchaV2 from "react-google-recaptcha";

import QuestionFieldRenderer from "~forms/components/question-field-renderer";
import CookieManager from "~common/lib/cookie-manager";

// We _do_ have to register them
QuestionFieldRenderer.register();

const styles = (theme: Theme): StyleRules => ({
  container: {
    width: "100%",
    marginBottom: 45,
  },
  headerPaper: {
    padding: 15,
  },
  headerTitle: {
    marginBottom: 15,
  },
  actionBar: {
    marginTop: 15,
    display: "flex",
    justifyContent: "flex-end",
  },
  actionBarButton: {
    marginLeft: 15,
  },
  dangerButton: {
    backgroundColor: "#af0202",
    marginLeft: 15,
    "&:hover": {
      backgroundColor: "#5D0A0A !important",
    },
  },
});

interface Props extends React.HTMLProps<HTMLDivElement> {
  history: any;
  match: any;
  formSelector: any;
  // The alternate selector when the dataURL or initDataURL errors, to show definitions/forms
  errorFormSelector?: any;
  form?: any;
  dataUrl: string;
  data?: any;
  onChangeAtPath?: (path: string, newVal: any, fieldDef?: any) => void;
  // PostOnly will not attempt to retrive the item from the form but will simply post to the data URL when submitted.
  postOnly: boolean;
  readOnly: boolean;
  clearDataOnSubmit: boolean;
  hideCancelButton: boolean;
  showSectionStatusIcon?: boolean;

  // the message to be shown to the user upon success
  successMessage?: any;
  successRenderer?: any;

  // If provided, redirectUrl where the user will be sent upon success
  redirectUrl?: string;

  // the starting data when making a new item
  initData?: any;
  initDataUrl?: string;

  customRenderers?: any;
  customMessages?: any;

  submitButtonText?: string;
  cancelButtonText?: string;

  allowDelete?: boolean;
  deleteButtonText?: string;
  confirmDeleteMessage?: string;

  confirmCancel?: boolean;
  confirmCancelMessage?: string;
  // This is meant to verify in client land and accept an error message back
  onBeforeSubmitAsyncVerify?: () => Promise<any>;
  onFileReject?: (rejectedFiles: Array<any>) => void;
  onAfterSubmitSuccess?: (data: any) => void;

  preventCollapse?: boolean;

  // allows the consumer to provide an array of field paths - these paths will be cached and fetched and applied when the component mounts.
  // key pattern for the cookie: `formBasedEditorCachePath|${path}|${window.location.pathname}`
  cachePaths?: Array<string>;

  // this is a passthrough to defintion section in the event a definition is not found
  noDefinitionFoundMessage?:string
}

type State = {
  status: "ready" | "loading" | "saving" | "error" | "errorForm" | "saved";
  id: any;
  data: any;
  formDefinition?: any;
  suppressWarnings?: boolean;
  policyMessages?: Array<any>;
  error?: any;
  successMessage?: any;
  confirmDelete?: boolean;
  confirmCancel?: boolean;
  requireCaptcha?: boolean;
  recaptchaVersion?: string;
};

class FormBasedEditor extends React.Component<
  Props & WithStyles<keyof typeof styles>,
  State
> {
  constructor(props: Props & WithStyles<keyof typeof styles>) {
    super(props, {});
    this.state = {
      id: this.props.match.params.id,
      data: props.initData || {},
      status: "loading",
      requireCaptcha: false,
      policyMessages: [],
    };
  }

  componentDidMount() {
    const { dataUrl, formSelector, postOnly, form, errorFormSelector, initDataUrl, cachePaths } =
      this.props;
    const { id } = this.state;
    var { data } = this.props;
    /*************************************************
     *
     *         Fetch data and definition
     *
     *************************************************/
    // Set up the calls (promises) to be performed, starting with a call to get the form.
    var promises = Array<any>();
    if (!form) {
      promises.push(
        request.get(
          "/api/form-definitions/select?selector=" +
            URLManager.getQueryStringValue(formSelector)
        )
      );
    }

    // If passed anything other then "new" then it should be an id.
    if (initDataUrl) {
      promises.push(request.get(initDataUrl));
    } else if (!data && !postOnly) {
      promises.push(request.get(dataUrl + "/" + id));
    }

    if (promises.length > 0) {
      Promise.all(promises)
        .then((values) => {
          let data: any = this.props.initData || {};
          // Values should have at least one element [0] that will be the form definition and may have 2 if editing and existing item
          switch (values.length) {
            case 2:
              const resp2 = values[1];
              // Eventually, we may want to look into non-200 error codes, especially 205 for init data on "/new" routes
              if (resp2.statusCode != 200) {
                // Get the definiton based 
                if (errorFormSelector) {
                  request.get(
                    "/api/form-definitions/select?selector=" +
                      URLManager.getQueryStringValue(errorFormSelector)
                  ).then((formResponse: request.Response)=>{
  
  
                    this.setState({ 
                      error: resp2.body, 
                      formDefinition: formResponse.body,
                      status: "errorForm" });
                  }).catch((err) => {
                    this.setState({ error: err, status: "ready" });
                  });
                } else {
                  this.setState({ error: resp2.body, status: "ready" });
                }
                
                return;
              }

              data = resp2.body;

              if (cachePaths && cachePaths.length > 0) {
                // loop over paths and check for a correspondong cookie
                cachePaths.forEach((path: string) => {
                  var ps = CookieManager.getCookie(
                    `formBasedEditorCachePath|${path}|${window.location.pathname}`
                  );
                  if (ps != "") {
                    // attempt to set the path value using cookie
                    try {
                      var parsed = JSON.parse(ps);
                      let svpResp = setValueAtPath(path, data, parsed);
                      data = svpResp.data;
                    } catch (e) {
                      // cached value just does not get applied
                    }
                  }
                });
              }

            // Fall through since if you have 2 you also have 1
            case 1:
              const resp = values[0];
              if (resp.statusCode != 200) {
                this.setState({ error: resp.body, status: "ready" });
                return;
              }

              const rv: string = resp.body.requireCaptchaVersion;
              const usingCaptcha: boolean =
                rv != null &&
                (window["App"].browserEnvironment || {})
                  .BROWSER_RECAPTCHA_SITE_KEY;

              this.setState(
                {
                  formDefinition: resp.body,
                  data: data,
                  status: "ready",
                  requireCaptcha: usingCaptcha,
                  recaptchaVersion: rv,
                },
                () => {
                  if (
                    rv == "r3" &&
                    usingCaptcha &&
                    (window["App"].browserEnvironment || {})
                      .BROWSER_RECAPTCHA_SITE_KEY &&
                    (window["App"].browserEnvironment || {})
                      .BROWSER_RECAPTCHA_SITE_KEY != ""
                  ) {
                    loadReCaptcha(
                      window["App"].browserEnvironment
                        .BROWSER_RECAPTCHA_SITE_KEY
                    );
                  }
                }
              );
              break;
            default:
              // should not be getting here unless something is very wrong
              this.setState({
                error: "An unknown error has occurred accessing the item",
                status: "ready",
              });
          }
        })
        .catch((err) => {
          if (errorFormSelector) {
            request.get(
              "/api/form-definitions/select?selector=" +
                URLManager.getQueryStringValue(errorFormSelector)
            ).then((formResponse: request.Response)=>{
              this.setState({ 
                error: err,
                formDefinition: formResponse.body,
                status: "errorForm" });
            }).catch((err) => {
              this.setState({ error: err, status: "ready" });
            });
          } else {
            this.setState({ error: err, status: "ready" });
          }
          
        });
    } else {
      // let data: any = this.props.initData || {}

      data = data || this.props.initData || {};

      if (cachePaths && cachePaths.length > 0) {
        // loop over paths and check for a correspondong cookie
        cachePaths.forEach((path: string) => {
          var ps = CookieManager.getCookie(
            `formBasedEditorCachePath|${path}|${window.location.pathname}`
          );
          if (ps != "") {
            // attempt to set the path value using cookie
            try {
              var parsed = JSON.parse(ps);
              let svpResp = setValueAtPath(path, data, parsed);
              data = svpResp.data;
            } catch (e) {
              // cached value just does not get applied
            }
          }
        });
      }

      this.setState({
        formDefinition: form,
        data: data,
        status: "ready",
      });
    }
  }

  UNSAFE_componentWillReceiveProps(
    newProps: Props & WithStyles<keyof typeof styles>,
    oldProps: Props & WithStyles<keyof typeof styles>
  ) {
    if (newProps.data && newProps.form) {
      let { data } = this.state;
      var newData: any = newProps.data;
      // This fixes additional saves when an ID has already been obtained but newProps.data does not reflect the new ID
      if (data && data.id && !newProps.data.id) {
        newData.id = data.id;
      }
      this.setState({
        data: newData,
        formDefinition: newProps.form,
      });
    }
  }

  difference(object, base) {
    function changes(object, base) {
      return _.transform(object, function (result, value, key) {
        if (!_.isEqual(value, base[key])) {
          result[key] =
            _.isObject(value) && _.isObject(base[key])
              ? changes(value, base[key])
              : value;
        }
      });
    }
    return changes(object, base);
  }

  // handleChangeAtPath is used to ferry changes from owned components upstream
  handleChangeAtPath = (path: string, value: any, fieldDef?: any) => {
    var { cachePaths } = this.props;
    if (cachePaths && cachePaths.length > 0) {
      if (cachePaths.indexOf(path) > -1) {
        CookieManager.setCookie(
          `formBasedEditorCachePath|${path}|${window.location.pathname}`,
          JSON.stringify(value),
          30
        );
      }
    }

    const fn = () => {
      let { data } = this.state;
      if (!this.props.onChangeAtPath) {
        // let { data } = this.state;
        let svpResp = setValueAtPath(path, data, value);
        data = svpResp.data;

        if (svpResp.err) {
          console.error(svpResp.err);
        }

        this.setState({ data }, () => {
          this.clearMessagesAtPath(path);
        });
      } else {
        this.props.onChangeAtPath(path, value, fieldDef);

        this.clearMessagesAtPath(path);
      }
      if (fieldDef && fieldDef.triggerValidationAtURL) {
        var triggerValidationAtURL = fieldDef.triggerValidationAtURL;
        if (triggerValidationAtURL.indexOf("?") > -1) {
          triggerValidationAtURL =
            triggerValidationAtURL + "&triggerPath=" + path;
        } else {
          triggerValidationAtURL =
            triggerValidationAtURL + "?triggerPath=" + path;
        }
        request
          .post(triggerValidationAtURL)
          .send(data)
          .then((response) => {
            let newData = this.state.data;

            let patch = response.body["diff"];

            let messages = this.state.policyMessages || [];

            let newMessages = response.body["policyMessages"];
            if (newMessages) {
              var ignoreMessagesAtPath = [];

              for (let newMessage of newMessages) {
                if (newMessage.severity == 3) {
                  ignoreMessagesAtPath.push(newMessage.path);
                }

                let duplicate = false;
                for (let message of messages) {
                  if (
                    message.message == newMessage.message &&
                    message.path == newMessage.path
                  ) {
                    duplicate = true;
                  }
                }
                if (duplicate != true) {
                  messages.push(newMessage);
                }
              }

              // Remove messages which have a reset status
              for (let p of ignoreMessagesAtPath) {
                messages = messages.filter((m) => {
                  return m.path != p;
                });
              }
            }
            let patchedData = jsonpatch.apply_patch(newData, patch);
            let focusSelector = response.body["focusSelector"];

            // Check that the data hasnt changed from underneath us while this call is happening.
            this.setState(
              { data: patchedData, policyMessages: messages },
              () => {
                var match = document.querySelector(focusSelector);
                if (match) {
                  match.focus();
                }
              }
            );
          })
          .catch((err) => {
            this.setState({ error: err });
          });
      }
    };
    if (this.state.error) {
      this.setState({ error: null }, () => {
        fn();
      });
    } else {
      fn();
    }
    // If form.field.ValidateUrl, do the call here, then set the state
    // Save the data and then show the user progress against errors
  };

  // reconciles policy messages when items are deleted from lists.
  handleItemDelete = (idx: any, path: string, data: any, fieldDef: any) => {
    var { policyMessages } = this.state;

    // filter out messages for the item that was just deleted.
    policyMessages = policyMessages.filter((pm: any) => {
      return pm.path.indexOf(`${path}/${idx}`) == -1;
    });

    // adjust the index for other rows
    policyMessages.forEach((pm: any) => {
      // identify remaining policy messages for the list
      if (pm.path.indexOf(`${path}/`) > -1) {
        var parts = pm.path.split("/");
        var pathIndex, partIndex;
        parts.forEach((p: string, pidx) => {
          var parsed = parseInt(p);
          // the part that is a number is the index that needs to be adjusted.
          if (!isNaN(parsed)) {
            pathIndex = parsed;
            partIndex = pidx;
          }
        });
        if (pathIndex) {
          if (pathIndex > idx) {
            pathIndex--;
          }
          parts[partIndex] = pathIndex;
          var newPath = parts.join("/");
          pm.path = newPath;
        }
      }
    });

    this.setState(
      {
        policyMessages: policyMessages,
      },
      () => {
        this.handleChangeAtPath(path, data, fieldDef);
      }
    );
  };

  handleDefinitionChange = (def: Definition) => {
    this.setState({ formDefinition: def });
  };

  handleEvaluateConditional = (
    fullPath: string,
    conditional: any,
    value: any,
    sectionData: any,
    field: PredefinedField
  ): any => {
    const { data } = this.state;
    // If the call has a conditional call that first, honoring the stopEvaluation as necessary
    var resp: any = {
      exprResult: false,
      stopEvaluation: false,
    };

    // if(conditional.path.indexOf("~/") == 0) {
    //   resp.stopEvaluation = true
    //   // Since this is something that we are only evaluating here (_new) we will stop evaluation when we are done.
    //   var conditionalPath = conditional.path.replace(/^~/, "")

    //   var evalVal = getValueAtPath(conditionalPath, data)

    //   resp.exprResult = baseConditionalEval(evalVal, conditional)

    // }

    return resp;
  };

  handleAccessRootData = (fullPath: string): any => {
    const { data } = this.state;
    // If the call has a conditional call that first, honoring the stopEvaluation as necessary

    var evalVal: any;

    // Since this is something that we are only evaluating here (_new) we will stop evaluation when we are done.
    fullPath = fullPath.replace(/^~/, "");

    evalVal = getValueAtPath(fullPath, data);

    return evalVal;
  };

  // clearMessagesAtPath helps to show user progress by clearing messages for a path as the user updates the value at the path
  clearMessagesAtPath = (path: string) => {
    const { policyMessages } = this.state;
    if (policyMessages && policyMessages.length > 0) {
      const pm = policyMessages.reduce((acc, m) => {
        let mp = m.path;
        if (mp.indexOf("/") == 0) {
          mp = mp.substring(1);
        }

        let op = path;
        if (op.indexOf("/") == 0) {
          op = op.substring(1);
        }

        if (mp != op) {
          acc.push(m);
        }

        return acc;
      }, []);

      this.setState({ policyMessages: pm });
    }
  };

  handleErrorClose = () => {
    // this.setState({ error: null })
  };

  handleSuccessMessageClose = () => {
    const { data } = this.state;
    const { postOnly, clearDataOnSubmit } = this.props;
    //10-07-2021 BD Removed an if statement that cleared data on the success close for post only. We figure data shouldnt be set to blank shouldn't be when the message closes, see handleSubmit
    this.setState({ successMessage: null });
    if (this.props.redirectUrl) {
      window.location.href = this.props.redirectUrl;
    }
  };

  handleCancel = () => {
    //reset the success message and clear the data
    if (this.props.confirmCancel) {
      this.setState({ error: null, confirmCancel: true });
    } else {
      if (this.props.redirectUrl) {
        window.location.href = this.props.redirectUrl;
      } else {
        // check url for redirectURL
        var redirectUrl = URLManager.getQueryStringValueByKey(
          "redirectUrl",
          window.location.href
        );
        if (redirectUrl && redirectUrl != "") {
          window.location.href = redirectUrl;
        } else {
          // look at referrer to see if it's within our domain
          if (
            document.referrer &&
            document.referrer.indexOf(window.location.host) > -1
          ) {
            this.props.history.goBack();
          } else {
            // if its not go to dashboard.
            window.location.href = "/";
          }
        }
      }
    }
  };

  handleCancelConfirm = () => {
    this.setState({ confirmCancel: false });
    if (this.props.redirectUrl) {
      window.location.href = this.props.redirectUrl;
    } else {
      window.location.href = "/";
    }
  };

  handleAsyncSubmit = () => {
    // If onBeforeSubmit<Promise> is not null, execute first and continue based on return
    if (this.props.onBeforeSubmitAsyncVerify) {
      this.props
        .onBeforeSubmitAsyncVerify()
        .then((response) => {
          this.handleSubmit();
        })
        .catch((err) => {
          this.setState({
            error: err,
          });
        });
    } else {
      this.handleSubmit();
    }
  };

  handleSubmit = () => {
    const { data, formDefinition } = this.state;
    const {
      postOnly,
      dataUrl,
      successMessage,
      formSelector,
      match,
      initDataUrl,
      onAfterSubmitSuccess,
    } = this.props;
    const self = this;
    let updateUrl = dataUrl;

    /*************************************************
     *
     *  Save the data, looking for policy messages
     *  on body when status is not OK
     *
     *************************************************/

    self.setState({ error: null, status: "saving" }, () => {
      // reset the policy messages, but hold on until the set state to enact
      //
      var subResp: any;
      // Determine whether to put or post based on a valid ID
      var fn: any = request.post;
      if (!postOnly && data.id) {
        fn = request.put;
        updateUrl = updateUrl + "/" + data.id;
      } else if (!postOnly && match.params.id) {
        fn = request.put;
        updateUrl = updateUrl + "/" + match.params.id;
      }

      // Inject the form key and selector in case the handler needs that.
      data["_formKey"] = formDefinition.key;
      data["_formSelector"] = formSelector;
      data["_suppressWarnings"] = self.state.suppressWarnings;

      // Perform the submit here
      fn(updateUrl)
        .send(data)
        .end(function (err, response) {
          if (err) {
            try {
              subResp = JSON.parse(response.text);
            } catch (e) {
              subResp = {};
            }

            self.setState({
              error: response.text,
              policyMessages: subResp.policyMessages,
              status: "ready",
            });
          } else {
            if (response.status !== 200) {
              //Handle response from server with not OK status
              self.setState({
                status: "ready",
                error: response.body,
                policyMessages: response.body,
              });
            } else {
              // Looks like we succeeded, so just need to clear the policy messages

              if (
                response.body.policyMessages &&
                response.body.policyMessages.length > 0
              ) {
                let hasWarning = false;
                for (let pm of response.body.policyMessages) {
                  if (pm.severity == 1) {
                    hasWarning = true;
                  }
                }
                if (response.body && response.body.data) {
                  self.handleChangeAtPath("", response.body.data);
                  self.setState({
                    status: "ready",
                    data: response.body.data,
                    suppressWarnings: hasWarning,
                    error: "One or more policy violations has occurred",
                    policyMessages: response.body.policyMessages,
                  });
                } else {
                  self.setState({
                    status: "ready",
                    suppressWarnings: hasWarning,
                    error: "One or more policy violations has occurred",
                    policyMessages: response.body.policyMessages,
                  });
                }
              } else {
                // We are successful.  At this point, if the server is asking for a redirection, do that.
                if (response.body.redirectToUrl) {
                  // Reiredt the user to the value that the server
                  window.location.href = response.body.redirectToUrl;
                  return;
                }
                if (response.body.redirectToReturnURL) {
                  // Reiredt the user to the value that the server
                  window.location.href = URLManager.getQueryStringValueByKey("returnURL", window.location.href);
                  return;
                }
                if (self.props.clearDataOnSubmit) {
                  var promises = Array<any>();

                  if (initDataUrl) {
                    promises.push(request.get(initDataUrl));
                  }
                  let data: any = self.props.initData || {};
                  if (promises.length > 0) {
                    Promise.all(promises)
                      .then((values) => {
                        const resp = values[0];
                        // Eventually, we may want to look into non-200 error codes, especially 205 for init data on "/new" routes
                        if (resp.statusCode != 200) {
                          self.setState({ error: resp.body, status: "ready" });
                          return;
                        }
                        data = resp.body;
                        if (resp.body) {
                          self.setState(
                            {
                              status: "saved",
                              data: data,
                              successMessage:
                                successMessage ||
                                "The item has saved successfully",
                              policyMessages: null,
                            },
                            () => {
                              if (onAfterSubmitSuccess) {
                                onAfterSubmitSuccess(response.body);
                              }
                            }
                          );
                        } else {
                          if (onAfterSubmitSuccess) {
                            onAfterSubmitSuccess(response.body);
                          }
                        }
                      })
                      .catch((err) => {
                        self.setState({ error: err, status: "ready" });
                      });
                  } else {
                    self.setState({
                      status: "saved",
                      data: data,
                      successMessage:
                        successMessage || "The item has saved successfully",
                      policyMessages: null,
                    });
                  }
                } else {
                  self.setState(
                    {
                      status: "saved",
                      data: response.body.data,
                      successMessage:
                        successMessage || "The item has saved successfully",
                      policyMessages: null,
                    },
                    () => {
                      if (onAfterSubmitSuccess) {
                        onAfterSubmitSuccess(response.body);
                      }
                    }
                  );
                }
              }
            }
          }
        });
    });
  };

  handleDeleteClick = () => {
    this.setState({ confirmDelete: true });
  };

  closeConfirmModal = () => {
    this.setState({ confirmDelete: false, confirmCancel: false });
  };

  handleDelete = () => {
    var { dataUrl, match } = this.props;
    this.setState({ status: "saving" }, () => {
      request
        .delete(`${dataUrl}/${match.params.id}`)
        .send(this.state.data)
        .then(() => {
          this.props.history.goBack();
        })
        .catch((err) => {
          this.setState({ status: "ready", error: err });
        });
    });
  };

  validateCaptcha = (token: string) => {
    if (!token) {
      this.setState({
        requireCaptcha: true,
      });
    } else {
      const body: any = {
        token: token,
      };
      request
        .post("/api/validate-captcha")
        .send(body)
        .set("Content-Type", "application/json")
        .then((response) => {
          this.setState({
            requireCaptcha: false,
          });
        });
    }
  };

  reCaptcha = () => {
    return new Promise((resolve, reject) => {
      (window as any).grecaptcha
        .execute(window["App"].browserEnvironment.BROWSER_RECAPTCHA_SITE_KEY, {
          action: "submit",
        })
        .then(function (token) {
          // Returning anything but null at the moment may need some work in form-based-editor.tsx
          resolve(null);
        })
        .catch((err) => {
          reject("Unable to verify user authenticity");
        });
    });
  };

  render() {
    const { classes }: { classes: any } = this.props;
    const {
      readOnly,
      customRenderers,
      submitButtonText,
      cancelButtonText,
      confirmCancelMessage,
      successRenderer,
      allowDelete,
      deleteButtonText,
      confirmDeleteMessage,
      match,
      showSectionStatusIcon,
      preventCollapse,
      noDefinitionFoundMessage,
    } = this.props;
    const {
      status,
      data,
      formDefinition,
      policyMessages,
      error,
      successMessage,
      confirmDelete,
      confirmCancel,
      requireCaptcha,
    } = this.state;
    const self = this;

    switch (status) {
      case "loading":
        return <LoadingView classes={{}} />;
        break;

      case "saved":
        if (successRenderer) {
          const Sr = RendererRegistry.find(successRenderer);
          if (Sr) {
            return (
              <div classes={{ container: classes.container }}>
                <Paper className={classes.headerPaper}>
                  <Sr data={data}></Sr>
                </Paper>
                <div className={classes.actionBar}>
                  <Button
                    className={classes.actionBarButton}
                    variant="contained"
                    color="primary"
                    onClick={this.handleCancel}
                    disabled={false}
                  >
                    {"Done"}
                  </Button>
                </div>
              </div>
            );
            // If we get here, we are done with the render so break,
            break;
          }
        }
      // If we do not have a success renderer, just fall through

      default:
        var errorView: any = null;
        if (error) {
          errorView = (
            <ErrorView
              onClose={this.handleErrorClose}
              classes={{}}
              error={error}
            />
          );
        }

        var successMessageView: any = null;
        if (successMessage) {
          successMessageView = (
            <SuccessMessageView
              onClose={this.handleSuccessMessageClose}
              classes={{}}
              message={successMessage}
            />
          );
        }

        var cancelButton: any = null;
        if (!this.props.hideCancelButton) {
          cancelButton = (
            <Button
              className={classes.actionBarButton}
              variant="contained"
              color="default"
              onClick={this.handleCancel}
              disabled={status === "saving"}
            >
              {" "}
              {cancelButtonText || "Cancel"}{" "}
            </Button>
          );
        }

        var deleteButton: any = null;
        if (allowDelete && match.params.id != "new") {
          deleteButton = (
            <Button
              className={classes.dangerButton}
              variant="contained"
              color="primary"
              onClick={this.handleDeleteClick}
              disabled={status === "saving"}
            >
              {deleteButtonText || "Delete"}
            </Button>
          );
        }

        var confirmDeleteModal: any = null;
        if (confirmDelete) {
          confirmDeleteModal = (
            <SimpleConfirmationModal
              terminal={true}
              open={true}
              confirmationMessage={
                confirmDeleteMessage || "Are you sure you want to delete this?"
              }
              onClose={this.closeConfirmModal}
              onConfirm={this.handleDelete}
            />
          );
        }

        var confirmCancelModal: any = null;
        if (confirmCancel) {
          confirmCancelModal = (
            <SimpleConfirmationModal
              terminal={true}
              open={true}
              confirmationMessage={
                confirmCancelMessage || "Are you sure you want to cancel?"
              }
              onClose={this.closeConfirmModal}
              onConfirm={this.handleCancelConfirm}
            />
          );
        }

        return (
          <div classes={{ container: classes.container }}>
            {confirmDeleteModal}
            {confirmCancelModal}
            {errorView}
            {successMessageView}
            <Paper className={classes.headerPaper}>
              <DefinitionSection
                className={(formDefinition || {key:'undefined'}).key}
                onFileReject={this.props.onFileReject}
                data={data}
                definition={formDefinition}
                path={""}
                policyMessages={policyMessages}
                onChangeAtPath={self.handleChangeAtPath}
                onDefinitionChange={self.handleDefinitionChange}
                onAccessRootData={self.handleAccessRootData}
                handleItemDelete={self.handleItemDelete}
                // onEvaluateConditional={self.handleEvaluateConditional}
                showSectionStatusIcon={showSectionStatusIcon}
                gridSpacing={4}
                customRenderers={customRenderers}
                preventCollapse={preventCollapse}
                noDefinitionFoundMessage={noDefinitionFoundMessage}
              />
            </Paper>
            <div className={classes.actionBar}>
              {cancelButton}
              {!readOnly && formDefinition && (
                <Button
                  className={classes.actionBarButton}
                  variant="contained"
                  color="primary"
                  // onClick={this.handleSubmit}
                  onClick={this.handleAsyncSubmit}
                  disabled={status === "saving" || status === "errorForm" || requireCaptcha}
                >
                  {submitButtonText || "Save"}
                </Button>
              )}
              {deleteButton}
            </div>
            {this.state.recaptchaVersion == "v2" &&
              (window["App"].browserEnvironment || {})
                .BROWSER_RECAPTCHA_SITE_KEY && (
                <ReCaptchaV2
                  sitekey={
                    window["App"].browserEnvironment.BROWSER_RECAPTCHA_SITE_KEY
                  }
                  onChange={(data) => {
                    this.setState(
                      {
                        requireCaptcha: true,
                      },
                      () => {
                        this.validateCaptcha(data);
                      }
                    );
                  }}
                />
              )}
            {this.state.recaptchaVersion == "r3" &&
              (window["App"].browserEnvironment || {})
                .BROWSER_RECAPTCHA_SITE_KEY &&
              (window["App"].browserEnvironment || {})
                .BROWSER_RECAPTCHA_SITE_KEY &&
              (window["App"].browserEnvironment || {})
                .BROWSER_RECAPTCHA_SITE_KEY != "" && (
                <ReCaptcha
                  action="unsolicited_registration"
                  sitekey={
                    window["App"].browserEnvironment.BROWSER_RECAPTCHA_SITE_KEY
                  }
                  verifyCallback={this.validateCaptcha}
                />
              )}
          </div>
        );
    }
  }
}

export default withStyles(styles)(FormBasedEditor);
